/**
 * Dialog for inspecting tool call details.
 * Shows a friendly summary by default with collapsible raw JSON.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Clock, Loader2, Code, ShieldCheck } from 'lucide-react';
import { DialogForm, DisclosureChevron, JsonViewer } from '../../../shared/components/common';
import { Badge } from '../../../components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../api/axiosInstance';
import type { ToolCallData } from './types';

const MODULE_LABELS: Record<string, string> = {
  website_hosting: 'Websites',
  tasks: 'Tasks',
  sales: 'Sales',
  recruiting: 'Recruiting',
  people: 'People',
  accounting: 'Accounting',
  products: 'Products',
  email: 'Email',
  file_management: 'Files',
  support: 'Support',
  contacts: 'Contacts',
};

/** Format a raw tool name into a human-friendly display name. */
function formatToolName(toolName: string): string {
  const rawName = toolName.includes('__')
    ? toolName.split('__').slice(1).join('__')
    : toolName;
  const apiIdx = rawName.indexOf('_api_v1_');
  const cleanName = apiIdx > 0 ? rawName.substring(0, apiIdx) : rawName;
  return cleanName.replace(/_/g, ' ');
}

/** Extract a friendly summary from input parameters. */
function summarizeInput(input: Record<string, unknown>): string[] {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(input)) {
    if (value == null || value === '') continue;
    const label = key.replace(/_/g, ' ');
    if (typeof value === 'string') {
      // Truncate long strings (like HTML content)
      const display = value.length > 120 ? `${value.substring(0, 120)}...` : value;
      lines.push(`${label}: ${display}`);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      lines.push(`${label}: ${String(value)}`);
    } else {
      lines.push(`${label}: (object)`);
    }
  }
  return lines;
}

/** Extract a friendly summary from tool output. */
function summarizeOutput(output: Record<string, unknown>): { text: string; isError: boolean } {
  // Check for MCP JSON-RPC response pattern
  const result = output?.result;
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const mcpResult = result as Record<string, unknown>;
    const inner = mcpResult.result;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const innerResult = inner as Record<string, unknown>;
      const isError = !!innerResult.isError;
      const content = innerResult.content;
      if (Array.isArray(content)) {
        const texts = content
          .filter((c): c is { text: string } => typeof c === 'object' && c !== null && 'text' in c)
          .map(c => c.text);
        if (texts.length > 0) {
          return { text: texts.join('\n'), isError };
        }
      }
    }
  }

  // Check for direct error
  if (output?.error) {
    return { text: String(output.error), isError: true };
  }

  // Check for data field (common API response pattern)
  if (output?.data) {
    if (typeof output.data === 'string') {
      return { text: output.data, isError: false };
    }
    const data = output.data as Record<string, unknown>;
    if (data.name || data.title) {
      return { text: `Updated: ${data.name || data.title}`, isError: false };
    }
    return { text: 'Operation completed successfully', isError: false };
  }

  return { text: 'Completed', isError: false };
}

interface ToolCallDetailDialogProps {
  open: boolean;
  onClose: () => void;
  /** Already-loaded tool call data (from message history) */
  toolCall?: ToolCallData;
  /** Tool call ID to fetch full details if not already loaded */
  toolCallId?: string;
  /** Session ID for fetching details */
  sessionId?: string;
}

export const ToolCallDetailDialog: React.FC<ToolCallDetailDialogProps> = ({
  open,
  onClose,
  toolCall: initialToolCall,
  toolCallId,
  sessionId,
}) => {
  const { t } = useTranslation('aiChat');
  const [showRawJson, setShowRawJson] = useState(false);

  // Fetch full details when initial data is incomplete (e.g., from streaming events
  // which only have basic fields like tool_name, status, error_message — no input/output).
  const initialHasFullData = !!(initialToolCall?.input_data || initialToolCall?.output_data);
  const needsFetch = open && !!toolCallId && !!sessionId && !initialHasFullData;
  const { data: fetchedToolCall, isLoading } = useQuery({
    queryKey: ['chat-tool-call', sessionId, toolCallId],
    queryFn: async (): Promise<ToolCallData | undefined> => {
      const res = await axiosInstance.get(`/api/v1/ai-chat/sessions/${sessionId}/tool-calls/${toolCallId}`);
      return res.data?.data;
    },
    enabled: needsFetch,
    staleTime: 5 * 60 * 1000,
  });

  // Prefer fetched data (complete from DB) over initial (possibly incomplete from streaming)
  const toolCall = fetchedToolCall ?? initialToolCall;
  const loading = needsFetch && isLoading;

  const friendlyName = useMemo(
    () => (toolCall ? formatToolName(toolCall.tool_name) : ''),
    [toolCall],
  );

  const moduleLabel = toolCall ? (MODULE_LABELS[toolCall.module] || toolCall.module.replace(/_/g, ' ')) : '';

  const inputSummary = useMemo(
    () => (toolCall?.input_data ? summarizeInput(toolCall.input_data) : []),
    [toolCall?.input_data],
  );

  const outputSummary = useMemo(
    () => (toolCall?.output_data ? summarizeOutput(toolCall.output_data) : null),
    [toolCall?.output_data],
  );

  // Determine effective status: check MCP isError even if backend said "success"
  const effectiveStatus = useMemo(() => {
    if (!toolCall) return 'running' as const;
    if (toolCall.status === 'error') return 'error' as const;
    if (outputSummary?.isError) return 'error' as const;
    return toolCall.status;
  }, [toolCall, outputSummary]);

  if (!toolCall && !loading) return null;

  const statusIcon = {
    running: <Loader2 className="h-4 w-4 animate-spin text-info" />,
    success: <Check className="h-4 w-4 text-success" />,
    error: <X className="h-4 w-4 text-destructive" />,
    pending_approval: <ShieldCheck className="h-4 w-4 text-warning" />,
  };

  const title = (
    <span className="flex items-center gap-2">
      {toolCall && statusIcon[effectiveStatus]}
      <span className="capitalize">{friendlyName || t('toolCallDetail.toolCall')}</span>
      {moduleLabel && (
        <Badge variant="secondary" className="text-xs font-normal">
          {moduleLabel}
        </Badge>
      )}
    </span>
  );

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={title}
      hideActions
      maxWidth="lg"
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : toolCall ? (
        <div className="space-y-4">
            {/* Metadata row */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <Badge
                variant={effectiveStatus === 'success' ? 'default' : effectiveStatus === 'error' ? 'destructive' : effectiveStatus === 'pending_approval' ? 'outline' : 'secondary'}
                className="text-[10px]"
              >
                {t(`toolCallDetail.status.${effectiveStatus}`)}
              </Badge>
              {toolCall.latency_ms != null && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{t('toolCallDetail.latencyMs', { value: toolCall.latency_ms })}</span>
                </div>
              )}
              {toolCall.created_at && (
                <span>{new Date(toolCall.created_at).toLocaleString()}</span>
              )}
            </div>

            {/* Error message */}
            {(toolCall.error_message || (outputSummary?.isError && outputSummary.text)) && (
              <div className="rounded-md bg-destructive/10 border border-destructive/40 p-3">
                <p className="text-sm text-destructive">
                  {toolCall.error_message || outputSummary?.text}
                </p>
              </div>
            )}

            {/* Friendly input summary */}
            {inputSummary.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1.5">{t('toolCallDetail.input')}</h4>
                <div className="text-xs bg-muted/50 rounded-md p-3 border space-y-0.5">
                  {inputSummary.map((line, i) => (
                    <div key={i} className="text-muted-foreground">
                      <span className="font-medium capitalize text-foreground">
                        {line.split(': ')[0]}:
                      </span>{' '}
                      {line.split(': ').slice(1).join(': ')}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Friendly output summary */}
            {outputSummary && !outputSummary.isError && (
              <div>
                <h4 className="text-sm font-medium mb-1.5">{t('toolCallDetail.output')}</h4>
                <JsonViewer
                  data={outputSummary.text}
                  maxHeight=""
                  copyLabel={t('toolCallDetail.output')}
                />
              </div>
            )}

            {/* Raw JSON (collapsible) */}
            {(toolCall.input_data || toolCall.output_data) && (
              <Collapsible open={showRawJson} onOpenChange={setShowRawJson}>
                <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <DisclosureChevron open={showRawJson} className="h-3 w-3" />
                  <Code className="h-3 w-3" />
                  {t('toolCallDetail.rawJson')}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-3">
                  {toolCall.input_data && Object.keys(toolCall.input_data).length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">{t('toolCallDetail.input')}</h4>
                      <JsonViewer
                        data={toolCall.input_data}
                        maxHeight=""
                        copyLabel={t('toolCallDetail.input')}
                      />
                    </div>
                  )}
                  {toolCall.output_data && Object.keys(toolCall.output_data).length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-muted-foreground mb-1">{t('toolCallDetail.output')}</h4>
                      <JsonViewer
                        data={toolCall.output_data}
                        maxHeight=""
                        copyLabel={t('toolCallDetail.output')}
                      />
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )}
        </div>
      ) : null}
    </DialogForm>
  );
};
